/**
 * The announcement strip.
 *
 * Staff posts land here, above everything. Dismissals are kept locally by id -
 * the server has no per-user read state for these, and asking it to grow one
 * for a banner would be the wrong trade.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useSession } from '@/stores/session';
import { IconButton } from './ui';
import * as Icons from '@/icons';
import { Icon } from '@/icons/Icon';
import { EMBED_KINDS } from './Embed';
import { go } from '@/lib/hash';

import type { Announcement } from '@/lib/types';

const SEEN_KEY = 'pogu.seenAnnouncements';

function seen(): string[] {
  try { return JSON.parse(localStorage.getItem(SEEN_KEY) ?? '[]') as string[]; }
  catch { return []; }
}

export function AnnouncementBar() {
  const me = useSession((s) => s.me);
  const [dismissed, setDismissed] = useState<string[]>(seen);

  const { data } = useQuery({
    queryKey: ['announcements'],
    queryFn: () => api.get<{ announcements: Announcement[] }>('/api/announcements'),
    enabled: !!me,
    staleTime: 5 * 60_000
  });

  // Only the newest undismissed one - a stack of banners is worse than none.
  const live = (data?.announcements ?? []).find((a) => !dismissed.includes(a.id));
  if (!live) return null;

  function dismiss(id: string) {
    const next = [...dismissed, id].slice(-50);
    setDismissed(next);
    try { localStorage.setItem(SEEN_KEY, JSON.stringify(next)); } catch { /* storage disabled */ }
  }

  const kind = EMBED_KINDS[live.kind] ?? EMBED_KINDS.info;

  return (
    <div
      // A strip, not a card: the full embed lives in the Pugo thread and this
      // is only the nudge that says go and read it. Same colour and icon
      // though, so the two read as the same announcement.
      className="soft-in relative flex flex-none items-start gap-3 border-b border-line-soft px-[var(--pad)] py-2.5"
      style={{
        ['--em' as string]: kind.accent,
        background: 'color-mix(in srgb, ' + kind.accent + ' 9%, var(--bg-1))'
      }}
    >
      <span aria-hidden className="absolute inset-y-0 left-0 w-[3px] bg-[var(--em)]" />

      <span className="mt-0.5 flex-none" style={{ color: kind.accent }}>
        <Icon name={kind.icon} size={16} />
      </span>

      <button
        type="button"
        onClick={() => go('chat/__pogu')}
        className="min-w-0 flex-1 text-left"
      >
        <span className="flex flex-wrap items-baseline gap-x-2">
          <strong className="text-[13.5px]">{live.title}</strong>
          {live.subtitle ? (
            <span className="text-[12px] text-mute">{live.subtitle}</span>
          ) : null}
        </span>
        {live.body ? (
          <span className="mt-0.5 line-clamp-2 block text-[12.5px] leading-snug text-dim">
            {live.body.replace(/[*`]/g, '')}
          </span>
        ) : null}
      </button>

      <IconButton label="Dismiss" className="h-7 w-7 flex-none" onClick={() => dismiss(live.id)}>
        <Icons.Close size={14} />
      </IconButton>
    </div>
  );
}
